import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { BarChart3, LogOut, Film, Scissors, Sparkles, Camera, Video } from "lucide-react";

const TIER_LABEL = { free: "Free", storage: "Storage", creator: "Creator", pro: "Pro" };
const TIER_COLOR = {
  free: "border-stone-700 text-stone-400",
  storage: "border-sky-700 text-sky-400 bg-sky-600/10",
  creator: "border-orange-700 text-orange-400 bg-orange-600/10",
  pro: "border-emerald-700 text-emerald-400 bg-emerald-600/10",
};

export function PortalShell({ children }) {
  const { user, tier, logout } = useAuth();
  const navigate = useNavigate();
  // Shooters (photographers / videographers) get a stripped-down shell —
  // no plan pill, no My Clips / Rodeo Box tabs. Just their own portal
  // + sign out. Everything on the shooter side lives at /portal/shooter.
  const isShooter = user?.role === "photographer" || user?.role === "videographer";
  const ShooterIcon = user?.role === "videographer" ? Video : Camera;

  return (
    <div className="min-h-screen bg-stone-950" data-testid="portal-shell">
      <header className="border-b border-stone-800">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between gap-6 flex-wrap">
          <div>
            <div className="text-orange-600 text-[10px] tracking-[0.3em] uppercase font-bold">Rodeo</div>
            <div className="font-bold text-stone-100 uppercase tracking-tight text-xl leading-none mt-0.5" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
              Cut Studio
            </div>
          </div>
          {isShooter ? (
            <nav className="flex items-center gap-1 text-xs uppercase tracking-[0.2em]">
              <PortalNav to="/portal/shooter" icon={ShooterIcon} label={user?.role === "videographer" ? "My Videos" : "My Photos"} />
            </nav>
          ) : (
            <nav className="flex items-center gap-1 text-xs uppercase tracking-[0.2em]">
              <PortalNav to="/portal/my-clips" icon={Film} label="My Clips" />
              <PortalNav to="/portal/studio" icon={Scissors} label="Rodeo Box" />
              <PortalNav to="/portal/stats" icon={BarChart3} label="Stats" />
            </nav>
          )}
          <div className="flex items-center gap-3">
            {!isShooter && tier && tier !== "free" && (
              <NavLink
                to="/portal/upgrade"
                className={`inline-flex items-center gap-1.5 border px-2 py-1 text-[10px] uppercase tracking-widest font-mono-tc ${TIER_COLOR[tier] || TIER_COLOR.free}`}
                data-testid={`tier-badge-${tier}`}
                title="Manage subscription"
              >
                <Sparkles size={10} /> {TIER_LABEL[tier] || "Free"}
              </NavLink>
            )}
            {isShooter && (
              <div className="inline-flex items-center gap-1.5 border border-sky-700 text-sky-400 bg-sky-600/10 px-2 py-1 text-[10px] uppercase tracking-widest font-mono-tc">
                <ShooterIcon size={10} /> {user?.role === "videographer" ? "Videographer" : "Photographer"}
              </div>
            )}
            <div className="hidden sm:block text-right">
              <div className="text-[10px] text-stone-500 uppercase tracking-widest">Signed in</div>
              <div className="text-sm">{user?.name || user?.email}</div>
            </div>
            <button
              onClick={async () => { await logout(); navigate("/login"); }}
              className="btn-secondary inline-flex items-center gap-1.5 text-xs"
              data-testid="portal-logout"
            >
              <LogOut size={12} /> Sign out
            </button>
          </div>
        </div>
      </header>
      <main className="max-w-[1200px] mx-auto px-6 md:px-10 py-10">{children}</main>
    </div>
  );
}

function PortalNav({ to, icon: Icon, label, exact }) {
  return (
    <NavLink
      to={to}
      end={exact}
      className={({ isActive }) =>
        `px-3 py-2 inline-flex items-center gap-1.5 border-b-2 -mb-px transition-colors ${
          isActive ? "border-orange-600 text-stone-100" : "border-transparent text-stone-500 hover:text-stone-200"
        }`
      }
      data-testid={`portal-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <Icon size={12} /> {label}
    </NavLink>
  );
}
